import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { CameraAlt, Delete as DeleteIcon, InsertPhoto, Upload as UploadIcon, ViewInAr } from "@mui/icons-material";
import { useColorMode } from "../ColorModeContext";


interface InputPanelProps {
  synchronization: string;
  videoRef: React.RefObject<HTMLVideoElement>;
  inImage: string ;
  handleDelete: () => void;
  getRootProps: () => any;
  getInputProps: () => any;
  defineSync: (_event: React.MouseEvent<HTMLElement>, newSync: string | null) => void;
  colorState: string;
}

const InputPanel: React.FC<InputPanelProps> = ({
  synchronization,
  videoRef,
  inImage,
  handleDelete,
  getRootProps,
  getInputProps,
  defineSync,
  colorState,
}) => {
  
  const {colorMode} = useColorMode();
  
  return (
    <div style={{marginTop:'15px'}}>
      {/* Title */}
      <div className="Title">
        <h1 className= {colorMode?"TopNumberDark":"TopNumberLight"}>1</h1>
        <h1 className="Title">Input image</h1>
      </div>
      <h4 className="SubTitle">Select reference image for the base geometry</h4>

      {/* Displays webcam view */}
      {synchronization === "camera" && (
        <Card sx={{ width: "100%", marginBottom: 2 }}>
          <CardMedia>
            <video ref={videoRef} autoPlay playsInline style={{ width: "100%" }} />
          </CardMedia>
          <CardContent sx={{ height: 0, p: 0 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", width: "100%", textAlign: "center" }}>
              Webcam live view
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Displays screen view */}
      {synchronization === "viewport" && (
        <Card sx={{ width: "100%", marginBottom: 2 }}>
          <CardMedia>
            <video ref={videoRef} autoPlay playsInline style={{ width: "100%" }} />
          </CardMedia>
          <CardContent sx={{ height: 0, p: 0 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", width: "100%", textAlign: "center" }}>
              Screen share live view
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Displays image input */}
      {synchronization === "image" && (
        <Card sx={{ width: "100%", mb: 2, position: "relative" }}>
          {inImage ? (
            <>
              <CardMedia component="img" image={inImage} alt="Uploaded Image" sx={{ maxHeight: "300px", objectFit: "contain" }} />
              <IconButton
                onClick={handleDelete}
                sx={{
                  position: "absolute",
                  top: 5,
                  right: 5,
                  color: "white",
                }}
              >
                <DeleteIcon />
              </IconButton>
            </>
          ) : (
            <Box
              {...getRootProps()}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 150,
                border: "none",
                borderRadius: 2,
                cursor: "pointer",
                textAlign: "center",
                p: 2,
                backgroundColor: inImage === "" && colorState === "error" ? 'red':'',
              }}
            >
              <input {...getInputProps()} />
              <UploadIcon fontSize="large" />
              <Typography>Drag & drop an image here, or click to select</Typography>
            </Box>
          )}
        </Card>
      )}

      {/* Sync mode begins */}
      <Stack spacing={0.5} sx={{ marginBottom: "10px" }}>
        <ToggleButtonGroup value={synchronization} exclusive onChange={defineSync} sx={{ display: "flex", flex: 1, overflow: "auto" }}>
          <ToggleButton value="image" aria-label="Image" sx={{ width: "100%" }}>
            <InsertPhoto /> Image
          </ToggleButton>
          <div className="separatorVertical" />
          <ToggleButton value="viewport" aria-label="Viewport" sx={{ width: "100%" }}>
            <ViewInAr /> Viewport
          </ToggleButton>
          <div className="separatorVertical" />
          <ToggleButton value="camera" aria-label="Camera" sx={{ width: "100%" }}>
            <CameraAlt /> Camera
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {/* Sync mode ends */}
    </div>
  );
};

export default InputPanel;
